export const AUTHENTICATED_INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
export const AUTHENTICATED_INACTIVITY_WARNING_BUFFER_MS = 60 * 1000;
export const AUTHENTICATED_ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "click",
  "keydown",
  "scroll",
  "touchstart",
  "pointerdown",
  "pointermove",
  "pointerup",
  "wheel",
];

const CAPTURE_ACTIVITY_LISTENER_OPTIONS = { capture: true, passive: true };

export function createInactivityTimeoutController({
  activityEvents = AUTHENTICATED_ACTIVITY_EVENTS,
  clearScheduledTimeout = clearTimeout,
  eventTarget,
  getNow = () => Date.now(),
  onActivity = null,
  onInactive,
  scheduleTimeout = setTimeout,
  timeoutMs = AUTHENTICATED_INACTIVITY_TIMEOUT_MS,
  warningBufferMs = AUTHENTICATED_INACTIVITY_WARNING_BUFFER_MS,
}) {
  if (!eventTarget || typeof eventTarget.addEventListener !== "function" || typeof eventTarget.removeEventListener !== "function") {
    throw new Error("Le contrôleur d'inactivité a besoin d'un EventTarget valide.");
  }

  if (typeof onInactive !== "function") {
    throw new Error("Le contrôleur d'inactivité a besoin d'un callback onInactive.");
  }

  let deadlineAt = null;
  let started = false;
  let timeoutId = null;
  let timeoutInFlight = false;

  function clearTimer() {
    if (timeoutId !== null) {
      clearScheduledTimeout(timeoutId);
      timeoutId = null;
    }

    deadlineAt = null;
  }

  function getSnapshot() {
    return {
      deadlineAt,
      timeoutMs,
      warningAt: deadlineAt === null
        ? null
        : deadlineAt - warningBufferMs,
      warningBufferMs,
    };
  }

  function armTimer() {
    if (!started) {
      return;
    }

    clearTimer();
    deadlineAt = getNow() + timeoutMs;
    timeoutId = scheduleTimeout(async () => {
      timeoutId = null;
      deadlineAt = null;

      if (!started || timeoutInFlight) {
        return;
      }

      timeoutInFlight = true;
      try {
        await onInactive(getSnapshot());
      } finally {
        timeoutInFlight = false;
        if (started && timeoutId === null) {
          armTimer();
        }
      }
    }, timeoutMs);
  }

  function resetActivity({ notify = false } = {}) {
    if (!started || timeoutInFlight) {
      return;
    }

    armTimer();
    if (notify && typeof onActivity === "function") {
      onActivity(getSnapshot());
    }
  }

  function handleActivity() {
    resetActivity({ notify: true });
  }

  return {
    getSnapshot,
    signalActivity: resetActivity,
    start() {
      if (started) {
        return;
      }

      started = true;
      activityEvents.forEach((eventName) => {
        eventTarget.addEventListener(eventName, handleActivity, CAPTURE_ACTIVITY_LISTENER_OPTIONS);
      });
      armTimer();
    },
    stop() {
      if (!started) {
        return;
      }

      started = false;
      clearTimer();
      activityEvents.forEach((eventName) => {
        eventTarget.removeEventListener(eventName, handleActivity, CAPTURE_ACTIVITY_LISTENER_OPTIONS);
      });
    },
  };
}